import React from 'react';
import Styled from './styled';
import {
    AgentIndicator,
    BotIndicator,
    TypingIndicator,
} from '../index';
import { MessageParticipantRole } from '@amzn/ring-cs-chatbot-service-model';

export type EntryProps = {
    /** Chat Entry indicator, defaults to 'CUSTOM_BOT'. */
    indicator?: MessageParticipantRole;
    /** Timestamp associated with the Chat Entry (ISO 8601 Format) */
    timestamp: number;
    /** Children content to display inside the chat bubble. */
    children: React.ReactNode | string;
    /** Support loading state for chat message */
    loading?: boolean;
};

/**
 * Entry component that functions as a wrapper for Chat Bubble's within (children).
 * Supports indicator and timestamp associated with each 'entry'
 * 
 * @param {EntryProps} props - The properties to configure the Entry component.
 * @returns {JSX.Element} The rendered Entry component.
 */
export const Entry: React.FC<EntryProps> = ({
    indicator = MessageParticipantRole.CUSTOM_BOT,
    timestamp,
    children,
    loading = false,
}) => {

    // Determine Chat bubble orientation
    const customer = indicator === MessageParticipantRole.CUSTOMER;

    const formatTimestamp = (timestamp: string | number): string => {
        const date = new Date(timestamp);
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
        const formattedHours = hours < 10 ? `0${hours}` : hours;
        return `${formattedHours}:${formattedMinutes}`;
    };

    const formattedTimestamp = formatTimestamp(timestamp);

    return (
        <Styled.Entry customer={customer}>
            {loading ?
                <Styled.Loader customer={customer}>
                    <TypingIndicator indicator={indicator} />
                </Styled.Loader> :
                indicator === MessageParticipantRole.CUSTOMER ?
                    <></> :
                    indicator === MessageParticipantRole.AGENT ?
                        <Styled.Indicator>
                            <AgentIndicator />
                        </Styled.Indicator> :
                        <Styled.Indicator>
                            <BotIndicator />
                        </Styled.Indicator>
            }
            {children}
            <Styled.Timestamp customer={customer}>
                {formattedTimestamp}
            </Styled.Timestamp>
        </Styled.Entry>
    );
};